import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { deleteMixedTrack, fetchMixedLibrary, renameMixedTrack, searchMixedLibrary } from "../../api/urstudio/mixingengineer.api";
import { showToast } from "../../helper/ShowToast";
import { getUrStudioQueryScope } from "./urstudioScope";

export const useMixesLibrary = (libraryType = "all") => {
    const access = useSelector((state) => state.auth?.access);
    const scope = getUrStudioQueryScope(access);

    return useQuery({
        queryKey: ["mixes-library", scope, libraryType],
        queryFn: () => fetchMixedLibrary({ type: libraryType }),
        keepPreviousData: true, // for smooth pagination
        retry: false,
        onError: (error) => {
            showToast(
                "error",
                error?.response?.data?.message || "Failed to load mixes library"
            );
        },
    });
};

export const useSearchMixesLibrary = (qParam, libraryType = "all") => {
    // accept either a string or an object: useSearchMixesLibrary('foo') or useSearchMixesLibrary({ q: 'foo' })
    const q = qParam && typeof qParam === 'object' ? qParam.q : qParam;
    const access = useSelector((state) => state.auth?.access);
    const scope = getUrStudioQueryScope(access);

    return useQuery({
        queryKey: ["search-mixes-library", scope, q, libraryType],
        queryFn: () => searchMixedLibrary({ q, type: libraryType }),
        enabled: Boolean(q), // only run when a query is provided
        keepPreviousData: true, // for smooth pagination
        staleTime: 60 * 1000,
        retry: false,
        onError: (error) => {
            showToast(
                "error",
                error?.response?.data?.message || "Failed to load mixes library"
            );
        },
    });
};

export const useDeleteMixedTrack = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // mutation receives ids (string like 'id1' or 'id1,id2')
        mutationFn: (ids) => deleteMixedTrack({ ids }),
        onSuccess: () => {
            // refresh mixes library and search results
            queryClient.invalidateQueries(["mixes-library"]);
            queryClient.invalidateQueries(["search-mixes-library"]);
            showToast("success", "Mixed track deleted");
        },
        onError: (err) =>
            showToast(
                "error",
                err?.response?.data?.message || "Failed to delete mixed track"
            ),
    });
};

export const useRenameMixedTrack = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // mutation expects a single object { id, body }
        mutationFn: ({ id, body }) => renameMixedTrack(id, body),
        onSuccess: () => {
            // refresh mixes library and search results
            queryClient.invalidateQueries(["mixes-library"]);
            queryClient.invalidateQueries(["search-mixes-library"]);
            showToast("success", "Mixed track renamed");
        },
        onError: (err) =>
            showToast(
                "error",
                err?.response?.data?.message || "Failed to rename mixed track"
            ),
    });
};
